import DOMPurify from 'dompurify';
import { logError } from '@src/services/error-logger';

interface Props {
  className?: string;
  contents: string;
}

const purify = DOMPurify();

// enable target=_blank for links (https://github.com/cure53/DOMPurify/issues/317)
purify.addHook('afterSanitizeAttributes', (node): void => {
  // set all elements owning target to target=_blank
  const href = node.getAttribute('href');
  if (href) {
    // prevent links to non-bordr.com domains
    const url = new URL(href, window.location.href);
    const domain = url.hostname.split('.').slice(-2).join('.');
    if (domain !== 'bordr.com') {
      logError(new Error(`Invalid link when sanitizing: ${href}`));
      node.setAttribute('href', 'https://www.bordr.com');
    }
    // default all links to open in a new tab
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * This component is used to render strings in their raw HTML form,
 * while also sanitizing them using DOMPurify to prevent any potential security vulnerabilities.
 * It takes in a className and an contents as props, and uses purify to sanitize the string
 * before rendering it using dangerouslySetInnerHTML.
 */
function SanitizeRaw({ className, contents }: Props): JSX.Element {
  return (
    <span
      className={className}
      // we're OK here since we're only setting it for translated strings
      // that we control and sanitizing them additonally
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: purify.sanitize(contents) }}
    />
  );
}

export default SanitizeRaw;
